import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAnalytics, isSupported, setConsent, type Analytics } from 'firebase/analytics';

const requiredEnvVars = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
] as const;

const missingVars = requiredEnvVars.filter((key) => !import.meta.env[key]);
if (missingVars.length > 0) {
  throw new Error(
    `Missing required Firebase environment variables: ${missingVars.join(', ')}. ` +
      'Please check your .env file or environment configuration.'
  );
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);

// Async because isSupported() probes IndexedDB / cookies (typically 20–100ms); null in unsupported envs.
// Events fired before init resolves go through a one-shot queue so we don't drop render-time crashes.
let analyticsInstance: Analytics | null = null;
let analyticsInitDone = false;
let pendingAnalyticsCalls: Array<() => void> = [];

const drainPending = () => {
  const queued = pendingAnalyticsCalls;
  pendingAnalyticsCalls = [];
  for (const fn of queued) fn();
};

if (firebaseConfig.measurementId) {
  void isSupported().then((supported) => {
    if (supported) {
      // GA4 Consent Mode: analytics-only product, no ads, no consent banner.
      // Default-grant analytics storage; explicitly deny every ad signal.
      setConsent({
        analytics_storage: 'granted',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
      analyticsInstance = getAnalytics(app);
    }
    analyticsInitDone = true;
    drainPending();
  });
} else {
  analyticsInitDone = true;
}

export const getAnalyticsInstance = (): Analytics | null => analyticsInstance;

export const runWhenAnalyticsReady = (fn: () => void): void => {
  if (analyticsInitDone) fn();
  else pendingAnalyticsCalls.push(fn);
};
